import React from 'react';
import { useTranslation } from 'react-i18next';

interface FileUploadProps {
  disabled?: boolean;
  onFileSelect: (file: File | null) => void;
  fileInfo?: { name: string; size: number; type: string } | null;
  formatFileSize: (bytes: number) => string;
}

// Types MIME audio autorisés
const ALLOWED_AUDIO_TYPES = [
  'audio/mp3',
  'audio/mpeg',
  'audio/wav',
  'audio/wave',
  'audio/ogg',
  'audio/x-ogg',
  'audio/m4a',
  'audio/x-m4a',
  'audio/aac',
  'audio/mp4',
  'audio/x-mp3',
  'audio/x-wav',
];

// Vérifier si le type MIME est autorisé
const isAudioFile = (type: string): boolean => {
  return ALLOWED_AUDIO_TYPES.includes(type);
};

export const FileUpload: React.FC<FileUploadProps> = ({
  disabled = false,
  onFileSelect,
  fileInfo,
  formatFileSize,
}) => {
  const { t } = useTranslation();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  // Gérer l'upload de fichier
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Vérifier si c'est un fichier audio
      if (!isAudioFile(file.type)) {
        setError(t('fileUpload.error'));
        onFileSelect(null);
        return;
      }
      
      onFileSelect(file);
    }
  };

  // Gérer le drag and drop
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setError(null);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      
      // Vérifier si c'est un fichier audio
      if (!isAudioFile(file.type)) {
        setError(t('fileUpload.error'));
        onFileSelect(null);
        return;
      }
      
      onFileSelect(file);
    }
  };

  // Gérer le clic sur la zone de drop
  const handleDropZoneClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div>
      {/* Zone de drag and drop */}
      <div
        className={`drop-zone ${isDragging ? 'dragging' : ''}`}
        onClick={handleDropZoneClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="audio/mp3,audio/wav,audio/ogg,audio/m4a,audio/*"
          onChange={handleFileUpload}
          disabled={disabled}
          style={{ display: 'none' }}
          ref={fileInputRef}
        />
        <p className="drop-zone-text">
          {t('fileUpload.dropZone')}
        </p>
        <p className="drop-zone-formats">
          {t('fileUpload.formats')}
        </p>
      </div>

      {/* Afficher les infos du fichier (si fournies par le parent) */}
      {fileInfo && (
        <div className="file-info">
          <span>{fileInfo.name}</span>
          <span>{formatFileSize(fileInfo.size)}</span>
        </div>
      )}

      {/* Afficher l'erreur si le fichier n'est pas un audio */}
      {error && <div className="error">{error}</div>}
    </div>
  );
};
